import type { ReactNode } from 'react';
import { useCallback, useState } from 'react';
import {
  faCircleCheck,
  faCircleExclamation,
  faCloudArrowUp,
  faFileLines,
  faXmark,
} from '@awesome.me/kit-4a6bf60416/icons/classic/regular';
import type { IconDefinition } from '@fortawesome/fontawesome-svg-core';
import type { FileRejection } from 'react-dropzone';
import { useDropzone } from 'react-dropzone';

import { Button } from '@/components/ui/button';
import { Icon } from '@/components/ui/icon';
import { Heading4, MutedText, Paragraph, SmallText } from '@/components/ui/typography';
import { cn } from '@/lib/utils';

interface DocumentUploadStepProps {
  title?: string;
  description?: string;
  accept: Record<string, string[]>;
  maxSize?: number;
  maxFiles?: number;
  multiple?: boolean;
  isProcessing?: boolean;
  onSubmit: (files: File[]) => void;
  onCancel?: () => void;
  submitLabel?: string;
  submitIcon?: IconDefinition;
  cancelLabel?: string;
  helperText?: string;
  footer?: ReactNode;
  errorMessage?: string | null;
}

export function DocumentUploadStep({
  title,
  description,
  accept,
  maxSize,
  maxFiles,
  multiple = false,
  isProcessing = false,
  onSubmit,
  onCancel,
  submitLabel = 'Continue',
  submitIcon,
  cancelLabel = 'Back',
  helperText,
  footer,
  errorMessage,
}: DocumentUploadStepProps) {
  const [files, setFiles] = useState<File[]>([]);
  const [rejectionError, setRejectionError] = useState<string | null>(null);

  const onDrop = useCallback(
    (acceptedFiles: File[]) => {
      if (acceptedFiles.length === 0) return;
      setRejectionError(null);
      setFiles((prev) => {
        const next = multiple ? [...prev, ...acceptedFiles] : [acceptedFiles[0]];
        if (maxFiles && next.length > maxFiles) {
          setRejectionError(`You can upload a maximum of ${maxFiles} files.`);
          return next.slice(0, maxFiles);
        }
        return next;
      });
    },
    [multiple, maxFiles],
  );

  const onDropRejected = useCallback(
    (rejections: FileRejection[]) => {
      const tooLarge = rejections.find((r) => r.errors.some((e) => e.code === 'file-too-large'));
      if (tooLarge && maxSize) {
        const limitMb = Math.round(maxSize / (1024 * 1024));
        setRejectionError(
          rejections.length > 1
            ? `Some files exceed the ${limitMb} MB limit.`
            : `"${tooLarge.file.name}" exceeds the ${limitMb} MB limit.`,
        );
        return;
      }
      const invalidType = rejections.find((r) =>
        r.errors.some((e) => e.code === 'file-invalid-type'),
      );
      if (invalidType) {
        setRejectionError(`"${invalidType.file.name}" is not a supported file type.`);
        return;
      }
      setRejectionError(rejections[0]?.errors[0]?.message ?? 'File could not be added.');
    },
    [maxSize],
  );

  const { getRootProps, getInputProps, isDragActive, open } = useDropzone({
    onDrop,
    onDropRejected,
    accept,
    maxSize,
    multiple,
    disabled: isProcessing,
    noClick: multiple && files.length > 0,
    noKeyboard: multiple && files.length > 0,
  });

  const hasFiles = files.length > 0;

  return (
    <div className="space-y-6">
      {(title || description) && (
        <div className="space-y-1">
          {title && <Heading4>{title}</Heading4>}
          {description && <MutedText className="text-sm">{description}</MutedText>}
        </div>
      )}

      {hasFiles && multiple ? (
        <div className="space-y-3">
          <input {...getInputProps()} />
          <div className="flex items-center gap-2 text-sm font-medium text-foreground">
            <Icon icon={faCircleCheck} className="size-4 text-emerald-600" />
            Documents uploaded
          </div>
          <ul className="space-y-2">
            {files.map((file, index) => (
              <li
                key={`${file.name}-${file.lastModified}-${index}`}
                className="flex items-center justify-between gap-3"
              >
                <Paragraph className="truncate text-sm font-semibold">{file.name}</Paragraph>
                <button
                  type="button"
                  className="text-sm font-medium text-destructive hover:underline disabled:opacity-50"
                  onClick={() => setFiles((prev) => prev.filter((_, i) => i !== index))}
                  disabled={isProcessing}
                >
                  Remove
                </button>
              </li>
            ))}
          </ul>
          {maxFiles === undefined || files.length < maxFiles ? (
            <button
              type="button"
              className="text-sm font-medium text-primary hover:underline disabled:opacity-50"
              onClick={open}
              disabled={isProcessing}
            >
              Upload another document
            </button>
          ) : null}
        </div>
      ) : hasFiles ? (
        <div className="flex items-center justify-between rounded-md border border-border bg-muted/30 px-4 py-3">
          <div className="flex min-w-0 items-center gap-3">
            <Icon icon={faFileLines} className="size-5 shrink-0 text-primary" />
            <div className="min-w-0">
              <Paragraph className="truncate text-sm font-medium">{files[0].name}</Paragraph>
              <SmallText className="text-muted-foreground">
                {(files[0].size / 1024).toFixed(1)} KB
              </SmallText>
            </div>
          </div>
          <Button
            type="button"
            size="icon-sm"
            variant="ghost"
            colorScheme="secondary"
            onClick={() => setFiles([])}
            disabled={isProcessing}
            aria-label="Remove file"
          >
            <Icon icon={faXmark} />
          </Button>
        </div>
      ) : (
        <div
          {...getRootProps()}
          className={cn(
            'flex h-[260px] cursor-pointer flex-col items-center justify-center gap-3 rounded-md border border-dashed text-center',
            isDragActive
              ? 'border-primary bg-primary/5'
              : 'border-primary/40 bg-primary/5 hover:border-primary',
            isProcessing && 'pointer-events-none opacity-60',
          )}
        >
          <input {...getInputProps()} />
          <Icon icon={faCloudArrowUp} className="size-8 text-primary" />
          <Heading4>Drag and drop files here,</Heading4>
          <MutedText className="text-sm">or</MutedText>
          <Button type="button" disabled={isProcessing}>
            Upload
          </Button>
          {helperText && (
            <SmallText className="text-muted-foreground text-balance px-8 leading-tight">
              {helperText}
            </SmallText>
          )}
        </div>
      )}

      {(rejectionError || errorMessage) && (
        <div
          role="alert"
          className="flex items-start gap-2 rounded-md border border-destructive/30 bg-destructive/5 px-3 py-2 text-sm text-destructive"
        >
          <Icon icon={faCircleExclamation} className="mt-0.5 size-4 shrink-0" />
          <span>{rejectionError ?? errorMessage}</span>
        </div>
      )}

      {footer}

      <div className="flex justify-end gap-2">
        {onCancel && (
          <Button
            type="button"
            variant="outline"
            colorScheme="secondary"
            onClick={onCancel}
            disabled={isProcessing}
          >
            {cancelLabel}
          </Button>
        )}
        <Button
          type="button"
          onClick={() => hasFiles && onSubmit(files)}
          disabled={!hasFiles || isProcessing}
        >
          {!isProcessing && submitIcon && <Icon icon={submitIcon} className="mr-2 size-4" />}
          {isProcessing ? 'Processing…' : submitLabel}
        </Button>
      </div>
    </div>
  );
}
